import { useCallback, useState } from 'react';
import { DialogueModal } from './DialogueModal';
import { ActionModal } from './ActionModal';
import { getStageByKey } from '@/lib/questEngine';
import * as engine from '@/lib/questEngine';
import { useAuthStore } from '@/stores/auth';
import type { QuestChoice, QuestDefinition, QuestStageAction } from '@/types/quests';

interface QuestRunnerProps {
  quest: QuestDefinition;
  characterId: string;
  initialStageKey: string;
  onComplete: (rewards: { doubloons?: number; xp?: number; marks?: number }) => void;
  onClose: () => void;
}

export function QuestRunner({
  quest,
  characterId,
  initialStageKey,
  onComplete,
  onClose,
}: QuestRunnerProps): JSX.Element | null {
  const [stageKey, setStageKey] = useState(initialStageKey);
  const [stageNum, setStageNum] = useState(0);
  const refreshCharacter = useAuthStore((s) => s.refreshCharacter);

  const stage = getStageByKey(quest, stageKey);

  const advance = useCallback(
    async (nextKey: string, choiceLabel?: string) => {
      const currentStageNum = stageNum;
      const newNum = stageNum + 1;
      setStageNum(newNum);

      if (nextKey === 'refused') {
        await engine.resetQuest(characterId, quest.id);
        onClose();
        return;
      }

      if (nextKey === quest.completionStage) {
        // Advance stage first, then complete — both via Edge Function.
        await engine.advanceQuestStage(
          characterId,
          quest.id,
          currentStageNum,
          newNum,
          stageKey,
          choiceLabel,
        );

        const isTutorial = quest.id.startsWith('tutorial_');
        const nextTutorialNum = isTutorial
          ? parseInt(quest.id.replace('tutorial_0', ''), 10) + 1
          : undefined;

        const ok = await engine.completeQuest(
          characterId,
          quest.id,
          newNum,
          quest.rewards,
          nextTutorialNum,
          quest.unlocks[0],
        );

        if (ok) {
          await refreshCharacter();
          onComplete(quest.rewards);
        }
        return;
      }

      // Normal stage advance via Edge Function.
      await engine.advanceQuestStage(
        characterId,
        quest.id,
        currentStageNum,
        newNum,
        stageKey,
        choiceLabel,
      );
      setStageKey(nextKey);
    },
    [characterId, onClose, onComplete, quest, refreshCharacter, stageKey, stageNum],
  );

  if (!stage) return null;

  if (stage.type === 'dialogue') {
    return (
      <DialogueModal
        npc={stage.npc}
        text={stage.text}
        choices={stage.choices}
        continueText={stage.continueText}
        onChoice={(choice: QuestChoice) => {
          void advance(choice.nextStage, choice.label);
        }}
        onContinue={() => {
          if (stage.nextStage) void advance(stage.nextStage);
        }}
        onClose={onClose}
      />
    );
  }

  if (stage.type === 'action') {
    const actionStage = stage as QuestStageAction;
    return (
      <ActionModal
        description={actionStage.description}
        buttonText={actionStage.buttonText}
        successText={actionStage.successText}
        rewards={actionStage.rewards}
        durationMs={actionStage.durationMs}
        onComplete={() => {
          void advance(actionStage.nextStage);
        }}
        onClose={onClose}
      />
    );
  }

  return null;
}
